import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { IntlService } from 'ember-intl';
import type {
  BoxStateEnum,
  LocationMeasurement,
  PaginatorInfo,
  Shipment,
  ShipmentStateEnum,
} from 'graphql-models';
import { SHIPMENT_STATE_TRANSLATIONS } from 'walden-fast/constants/state-translations';
import type { AppServicesWaldenBoxListModel } from 'walden-fast/routes/app/services/walden-box/list';
import type { SortDirection } from 'walden-fast/types/pagination';

export default class AppServicesWaldenBoxListController extends Controller {
  declare model: AppServicesWaldenBoxListModel;

  @service('intl') declare intlService: IntlService;

  @tracked search: string | undefined;
  @tracked page = 1;
  @tracked pageSize = 20;
  @tracked sortField: string | undefined;
  @tracked sortDirection: SortDirection = 'none';

  @tracked state: string | undefined;
  @tracked consignee: string | undefined;

  queryParams = [
    'search',
    'page',
    'pageSize',
    'sortField',
    'sortDirection',
    'state',
    'consignee',
  ];

  get shipments() {
    return this.model.lastSuccessful?.value?.shipmentsForShipper?.data || [];
  }

  get paginatorInfo() {
    return (
      this.model.lastSuccessful?.value?.shipmentsForShipper?.paginatorInfo ||
      ({} as PaginatorInfo)
    );
  }

  get states() {
    return [
      {
        name: this.intlService.t('pages.waldenBox.list.filters.state.ok'),
        value: 'OK',
      },
      {
        name: this.intlService.t('pages.waldenBox.list.filters.state.notOk'),
        value: 'NOK',
      },
    ];
  }

  get consignees() {
    return this.model.lastSuccessful?.value?.consignees || [];
  }

  @action
  clearFilters() {
    this.state = undefined;
    this.consignee = undefined;
  }

  @action
  sort(field: string, direction: SortDirection) {
    this.sortField = field;
    this.sortDirection = direction;
  }

  sortDirectionForField = (field: string) => {
    return this.sortField === field ? this.sortDirection : 'none';
  };

  errorState = (shipment: Shipment) => {
    return shipment.alerts.length > 0;
  };

  stateLabel = (state: ShipmentStateEnum) => {
    return this.intlService.t(SHIPMENT_STATE_TRANSLATIONS[state]);
  };

  boxStateLabel = (state: BoxStateEnum) => {
    const BOX_STATE_TRANSLATIONS: Record<BoxStateEnum, string> = {
      ACTIVE: this.intlService.t('pages.waldenBox.list.table.boxState.active'),
      SLEEP: this.intlService.t('pages.waldenBox.list.table.boxState.sleep'),
    };
    return BOX_STATE_TRANSLATIONS[state];
  };

  errorTemperature = (shipment: Shipment) => {
    return (
      shipment.minTemperature &&
      shipment.maxTemperature &&
      shipment.currentTemperature &&
      (shipment.currentTemperature.valueCelsius < shipment.minTemperature ||
        shipment.currentTemperature.valueCelsius > shipment.maxTemperature)
    );
  };

  googleMapsUrl = (location: LocationMeasurement) => {
    return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
  };

  errorBattery = (shipment: Shipment) => {
    // todo: no idea what the logic for the treshold should be https://www.figma.com/design/eHQ2v86Du0vRCvnwtdSTdV?node-id=3338-3033&m=dev#1357612270

    const percent = shipment.currentBattery?.percent;
    return percent !== null && percent !== undefined && percent < 20;
  };
}
